/**
 *
 *  Dropdown
 *  TruBe Component
 *
 *  Created by Vladimir Shishlyannikov on 14/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";

import { isMobile } from "../../utils";

const defaultProps = {
  noBorder: false,
  position: "absolute",
  placement: "default",
  rounded: "bottom",
  showArrow: false,
  keepOpen: false,
};

const positionClassesMap = {
  relative: "tbk-relative",
  absolute: "tbk-absolute",
  fixed: "tbk-fixed",
  static: "tbk-static",
};

const Dropdown = ({
  children,
  className,
  listboxId,
  buttonElement: ButtonElement,
  buttonElementRef,
  buttonElementProps,
  position,
  placement,
  rounded,
  showArrow,
  keepOpen,
  noBorder,
  openOnHover,
  disabled,
  open,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const dropdownElementRef = useRef();
  const isOpenRef = useRef();
  const timeoutRef = useRef();

  useEffect(() => {
    if (open && !isOpenRef.current) {
      setIsOpen(true);
      isOpenRef.current = true;
    } else if (!open && isOpenRef.current) {
      setIsOpen(false);
      isOpenRef.current = false;
    }

    return () => {};
  }, [open]);

  const _close = useCallback(() => {
    setIsOpen(false);
    isOpenRef.current = false;

    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    });
  }, [onClose]);

  const _open = useCallback(() => {
    setIsOpen(true);
    isOpenRef.current = true;

    setTimeout(() => {
      if (onOpen) {
        onOpen();
      }
    });
  }, [onOpen]);

  const _onWindowClick = useCallback(
    (e) => {
      const dropdownElement = dropdownElementRef.current;
      const isOpen = isOpenRef.current;

      if (
        !keepOpen &&
        e.target !== dropdownElement &&
        !(dropdownElement && dropdownElement.contains(e.target)) &&
        isOpen
      ) {
        _close();
      }
    },
    [_close, keepOpen]
  );

  useEffect(() => {
    window.addEventListener("pointerdown", _onWindowClick);

    return () => {
      // Invoke close in case someone rely on it
      if (onClose) {
        onClose();
      }

      window.removeEventListener("pointerdown", _onWindowClick);
    };
  }, [_onWindowClick, onClose]);

  const _onToggleClick = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (isOpen) {
        _close();
      } else {
        _open();
      }
    },
    [isOpen, _close, _open]
  );

  const _onMouseEnter = useCallback(() => {
    if (isMobile) {
      return;
    }
    clearTimeout(timeoutRef.current);
    setIsHovering(true);
    _open();
  }, _open);

  const _onMouseLeave = useCallback(() => {
    if (isMobile) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      _close();
    }, 150); // Задержка в 300 мс перед закрытием
  }, _close);

  // TODO: add listbox manipulations (tabindex, ARIA)
  // create component classes
  const dropdownClasses = `tb-dropdown tbk-relative${isOpen ? " tb-open" : ""}${
    disabled ? " disabled" : ""
  }${className ? " " + className : ""}`;

  const dropdownMenuClasses = `tb-dropdown-menu ${
    positionClassesMap[position]
  } ${placement === "default" ? "tbk-left-0 tbk-right-0" : placement} ${
    isOpen ? "tbk-animate-dropdown-menu-open" : ""
  } ${
    rounded === "bottom"
      ? "tbk-rounded-b-2xl"
      : "tbk-rounded-t-2xl tbk-rounded-b-2xl"
  } ${
    noBorder ? "" : "tbk-border tbk-border-solid tbk-border-blue-grey-light"
  } tbk-bg-basic-white_ tbk-backdrop-blur-xl_ tbk-shadow-trube tbk-z-1005 tbk-overflow-y-auto tbk-will-change-background`;

  return (
    <div
      ref={dropdownElementRef}
      className={`${dropdownClasses}${
        showArrow ? " tb-dropdown-with-arrow" : ""
      }`}
      role="combobox"
      aria-controls={listboxId}
      aria-expanded={isOpen}
      aria-disabled={disabled}
      aria-label="Open menu"
      aria-haspopup="menu"
      onMouseEnter={openOnHover ? _onMouseEnter : undefined}
      onMouseLeave={openOnHover ? _onMouseLeave : undefined}
      onClick={openOnHover ? undefined : _onToggleClick}
    >
      <div
        className="tb-dropdown-toggle tbk-cursor-pointer"
        onClick={_onToggleClick}
      >
        {ButtonElement ? (
          <ButtonElement
            ref={buttonElementRef}
            open={isOpen}
            {...buttonElementProps}
          />
        ) : (
          <button type="button" className="tb-dropdown-button" />
        )}
      </div>
      <div
        role="menu"
        className={dropdownMenuClasses}
        style={{
          display: isOpen ? "block" : "none",
        }}
      >
        <div className="tb-dropdown-container">{children}</div>
      </div>
    </div>
  );
};

Dropdown.displayName = "Dropdown";
Dropdown.defaultProps = defaultProps;

Dropdown.propTypes = {
  listboxId: PropTypes.string,
  buttonElement: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  position: PropTypes.string,
  placement: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  showArrow: PropTypes.bool,
  keepOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default Dropdown;
